<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const page = usePage();

const props = defineProps({
    recipient: {
        type: Object,
        required: true
    },
    readOnly: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['updated']);

const isEditingOwner = ref(false);
const isEditingSeller = ref(false);
const savingOwner = ref(false);
const savingSeller = ref(false);

const ownerForm = ref({
    name: '',
    email: '',
    phone: '',
});

const sellerForm = ref({
    name: '',
    email: '',
    phone: '',
});

const ownerErrors = ref({});
const sellerErrors = ref({});

const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const ownerEmailError = computed(() => {
    if (ownerErrors.value.email) return ownerErrors.value.email;
    if (!ownerForm.value.email) return $t('Email is required');
    if (!isValidEmail(ownerForm.value.email)) return $t('Please enter a valid email address');
    return null;
});

const sellerEmailError = computed(() => {
    if (sellerErrors.value.email) return sellerErrors.value.email;
    if (!sellerForm.value.email) return $t('Email is required');
    if (!isValidEmail(sellerForm.value.email)) return $t('Please enter a valid email address');
    return null;
});

const isOwnerFormValid = computed(() => ownerForm.value.email && isValidEmail(ownerForm.value.email));
const isSellerFormValid = computed(() => sellerForm.value.email && isValidEmail(sellerForm.value.email));

const needsOwnerEmail = computed(() => {
    return props.recipient.email_target === 'owner' && !props.recipient.owner_email;
});

const needsSellerEmail = computed(() => {
    return props.recipient.email_target === 'seller' && !props.recipient.seller_email;
});

const startEditOwner = () => {
    ownerForm.value = {
        name: props.recipient.owner_name || '',
        email: props.recipient.owner_email || '',
        phone: props.recipient.owner_phone || '',
    };
    ownerErrors.value = {};
    isEditingOwner.value = true;
};

const startEditSeller = () => {
    sellerForm.value = {
        name: props.recipient.seller_name || '',
        email: props.recipient.seller_email || '',
        phone: props.recipient.seller_phone || '',
    };
    sellerErrors.value = {};
    isEditingSeller.value = true;
};

const saveOwner = () => {
    if (!isOwnerFormValid.value) return;

    savingOwner.value = true;
    ownerErrors.value = {};

    router.put(route('orders.update-recipient-contact'), {
        target: 'owner',
        site_id: props.recipient.site_id,
        name: ownerForm.value.name,
        email: ownerForm.value.email,
        phone: ownerForm.value.phone,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            isEditingOwner.value = false;
            emit('updated');
        },
        onError: (errors) => {
            ownerErrors.value = errors;
        },
        onFinish: () => {
            savingOwner.value = false;
        },
    });
};

const saveSeller = () => {
    if (!isSellerFormValid.value) return;

    savingSeller.value = true;
    sellerErrors.value = {};

    router.put(route('orders.update-recipient-contact'), {
        target: 'seller',
        seller_id: props.recipient.seller_id,
        name: sellerForm.value.name,
        email: sellerForm.value.email,
        phone: sellerForm.value.phone,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            isEditingSeller.value = false;
            emit('updated');
        },
        onError: (errors) => {
            sellerErrors.value = errors;
        },
        onFinish: () => {
            savingSeller.value = false;
        },
    });
};

const cancelEditOwner = () => { isEditingOwner.value = false; ownerErrors.value = {}; };
const cancelEditSeller = () => { isEditingSeller.value = false; sellerErrors.value = {}; };
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Seller Block -->
        <div class="p-3 rounded-lg border"
            :class="recipient.type === 'seller'
                ? 'bg-orange-50 border-orange-200'
                : needsSellerEmail
                    ? 'bg-red-50 border-red-300'
                    : 'bg-surface-50 border-surface-200'">

            <div class="flex items-center justify-between mb-1">
                <label class="block text-sm font-medium text-surface-700">{{ $t('Seller') }}</label>
                <button v-if="!readOnly && !isEditingSeller" @click="startEditSeller" class="text-xs text-primary-500 hover:text-primary-700 cursor-pointer">
                    <i class="pi pi-pencil mr-1"></i>{{ $t('Edit') }}
                </button>
            </div>

            <!-- View Mode -->
            <template v-if="readOnly || !isEditingSeller">
                <p class="text-surface-900 font-medium">{{ recipient.seller_name || '-' }}</p>
                <p class="text-xs text-surface-500">{{ recipient.seller_email || '-' }}</p>
                <p v-if="recipient.seller_phone" class="text-xs text-surface-400">{{ recipient.seller_phone }}</p>
                <Tag v-if="recipient.type === 'seller'" :value="$t('Receive the email')" severity="warn" icon="pi pi-envelope" class="mt-2 text-xs" />

                <!-- Warning: needs email -->
                <div v-if="needsSellerEmail" class="mt-2 p-2 bg-red-100 rounded text-xs text-red-700 flex items-start gap-1">
                    <i class="pi pi-exclamation-triangle mt-0.5"></i>
                    <span>{{ $t('Email is required to send publication request. Please update the seller email.') }}</span>
                </div>
            </template>

            <!-- Edit Mode -->
            <template v-else>
                <div class="space-y-2 mt-2">
                    <div>
                        <label class="block text-xs text-surface-500 mb-0.5">{{ $t('Name') }}</label>
                        <InputText v-model="sellerForm.name" class="w-full p-inputtext-sm" />
                    </div>
                    <div>
                        <label class="block text-xs text-surface-500 mb-0.5">Email *</label>
                        <InputText v-model="sellerForm.email" type="email" class="w-full p-inputtext-sm" :class="{ 'p-invalid': sellerForm.email && !isValidEmail(sellerForm.email) }" />
                        <small v-if="sellerForm.email && !isValidEmail(sellerForm.email)" class="text-red-600 text-xs mt-1 block">
                            <i class="pi pi-exclamation-circle mr-1"></i>{{ $t('Please enter a valid email address') }}
                        </small>
                        <small v-else-if="sellerErrors.email" class="text-red-600 text-xs mt-1 block">
                            <i class="pi pi-exclamation-circle mr-1"></i>{{ sellerErrors.email }}
                        </small>
                    </div>
                    <div>
                        <label class="block text-xs text-surface-500 mb-0.5">{{ $t('Phone') }}</label>
                        <InputText v-model="sellerForm.phone" class="w-full p-inputtext-sm" />
                    </div>
                    <div class="flex gap-2 mt-2">
                        <Button @click="saveSeller" :disabled="!isSellerFormValid" :loading="savingSeller" :label="$t('Save')" icon="pi pi-check" size="small" severity="success" />
                        <Button @click="cancelEditSeller" :label="$t('Cancel')" icon="pi pi-times" size="small" severity="secondary" outlined />
                    </div>
                </div>
            </template>
        </div>

        <!-- Owner Block -->
        <div class="p-3 rounded-lg border"
            :class="recipient.type === 'owner'
                ? 'bg-blue-50 border-blue-200'
                : needsOwnerEmail
                    ? 'bg-red-50 border-red-300'
                    : 'bg-surface-50 border-surface-200'">

            <div class="flex items-center justify-between mb-1">
                <label class="block text-sm font-medium text-surface-700">{{ $t('Owner') }}</label>
                <button v-if="!readOnly && !isEditingOwner" @click="startEditOwner" class="text-xs text-primary-500 hover:text-primary-700 cursor-pointer">
                    <i class="pi pi-pencil mr-1"></i>{{ $t('Edit') }}
                </button>
            </div>

            <!-- View Mode -->
            <template v-if="readOnly || !isEditingOwner">
                <p class="text-surface-900 font-medium">{{ recipient.owner_name || '-' }}</p>
                <p class="text-xs text-surface-500">{{ recipient.owner_email || '-' }}</p>
                <p v-if="recipient.owner_phone" class="text-xs text-surface-400">{{ recipient.owner_phone }}</p>
                <Tag v-if="recipient.type === 'owner'" :value="$t('Receive the email')" severity="info" icon="pi pi-envelope" class="mt-2 text-xs" />

                <!-- Warning: needs email -->
                <div v-if="needsOwnerEmail" class="mt-2 p-2 bg-red-100 rounded text-xs text-red-700 flex items-start gap-1">
                    <i class="pi pi-exclamation-triangle mt-0.5"></i>
                    <span>{{ $t('Email is required to send publication request. Please update the site owner email.') }}</span>
                </div>
            </template>

            <!-- Edit Mode -->
            <template v-else>
                <div class="space-y-2 mt-2">
                    <div>
                        <label class="block text-xs text-surface-500 mb-0.5">{{ $t('Name') }}</label>
                        <InputText v-model="ownerForm.name" class="w-full p-inputtext-sm" />
                    </div>
                    <div>
                        <label class="block text-xs text-surface-500 mb-0.5">Email *</label>
                        <InputText v-model="ownerForm.email" type="email" class="w-full p-inputtext-sm" :class="{ 'p-invalid': ownerForm.email && !isValidEmail(ownerForm.email) }" />
                        <small v-if="ownerForm.email && !isValidEmail(ownerForm.email)" class="text-red-600 text-xs mt-1 block">
                            <i class="pi pi-exclamation-circle mr-1"></i>{{ $t('Please enter a valid email address') }}
                        </small>
                        <small v-else-if="ownerErrors.email" class="text-red-600 text-xs mt-1 block">
                            <i class="pi pi-exclamation-circle mr-1"></i>{{ ownerErrors.email }}
                        </small>
                    </div>
                    <div>
                        <label class="block text-xs text-surface-500 mb-0.5">{{ $t('Phone') }}</label>
                        <InputText v-model="ownerForm.phone" class="w-full p-inputtext-sm" />
                    </div>
                    <div class="flex gap-2 mt-2">
                        <Button @click="saveOwner" :disabled="!isOwnerFormValid" :loading="savingOwner" :label="$t('Save')" icon="pi pi-check" size="small" severity="success" />
                        <Button @click="cancelEditOwner" :label="$t('Cancel')" icon="pi pi-times" size="small" severity="secondary" outlined />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
